import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  ANY_REACTION_COMPONENT_ROLE_LABEL,
  CHEM_SEARCH_OPERATOR_TO_DISPLAY_NAME,
  SIMILARITY_SCORE_CUTOFFS,
} from 'chemistry/ChemistryConstants';
import {getSimilarityOptionLabel, ToggleHighlightParams} from 'chemistry/ChemSearchUtils';
import {
  getReactionComponentRoleOptionLabel,
  REACTION_COMPONENT_ROLE_UI_ORDER,
} from 'chemistry/ReactionChemSearchUtils';
import {
  CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME,
  ChemistryReactionRole,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  CHEMICAL_STRUCTURE_SEARCH_OPERATORS,
  ChemicalStructureSearchOperator,
  Operator,
} from 'search/SearchConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import ToggleGroup from 'ui-library/ToggleGroup';

interface ChemSearchTypeToggleProps {
  chemSearchOperator: ChemicalStructureSearchOperator;
  setChemSearchOperator: (operator: ChemicalStructureSearchOperator) => void;
}

export function ChemSearchTypeToggle({
  chemSearchOperator,
  setChemSearchOperator,
}: ChemSearchTypeToggleProps): JSX.Element {
  return (
    <FormGroup>
      <FieldLabel label="Specify search options" />
      <ToggleGroup
        isFullWidth={true}
        size={ButtonSize.SMALL}
        options={CHEMICAL_STRUCTURE_SEARCH_OPERATORS.map((operator) => ({
          id: operator,
          buttonProps: {text: CHEM_SEARCH_OPERATOR_TO_DISPLAY_NAME[operator]},
        }))}
        selection={chemSearchOperator}
        onChangeSelection={setChemSearchOperator}
      />
    </FormGroup>
  );
}

interface ReactionRoleSelectorProps {
  reactionComponentRole: ChemistryReactionRole | null;
  setReactionComponentRole: (role: ChemistryReactionRole | null) => void;
}

export function ReactionRoleSelector({
  reactionComponentRole,
  setReactionComponentRole,
}: ReactionRoleSelectorProps): JSX.Element {
  return (
    <FormGroup>
      <FieldLabel label="Specify reaction role" />
      <SelectDropdown
        alwaysDropDown={true}
        dataTestAttrs={{component: 'ChemSearchModalOptions', element: 'reactionRoleDropdown'}}
        displayName={getReactionComponentRoleOptionLabel(reactionComponentRole)}
        menu={
          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={reactionComponentRole}>
            <Option
              dataTestAttrs={{component: 'ChemSearchModalOptions', element: 'reactionRoleOption', key: 'any'}}
              value={null}
              onSelect={setReactionComponentRole}
              key="any"
            >
              {ANY_REACTION_COMPONENT_ROLE_LABEL}
            </Option>
            {REACTION_COMPONENT_ROLE_UI_ORDER.map((role) => (
              <Option
                dataTestAttrs={{
                  component: 'ChemSearchModalOptions',
                  element: 'reactionRoleOption',
                  key: role,
                }}
                value={role}
                onSelect={setReactionComponentRole}
                key={role}
              >
                {CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role]}
              </Option>
            ))}
          </Menu>
        }
      />
    </FormGroup>
  );
}

interface ChemSearchMoleculeOptionsProps {
  highlightChecked: boolean;
  setHighlightChecked: (highlightChecked: boolean) => void;
  chemSearchOperator: ChemicalStructureSearchOperator;
  minSimilarity: number;
  setMinSimilarity: (minSimilarity: number) => void;
  toggleHighlightParams: ToggleHighlightParams;
}

export function ChemSearchMoleculeOptions({
  highlightChecked,
  setHighlightChecked,
  chemSearchOperator,
  minSimilarity,
  setMinSimilarity,
  toggleHighlightParams,
}: ChemSearchMoleculeOptionsProps): JSX.Element | null {
  const highlightOption = toggleHighlightParams.showToggleHighlightOption ? (
    <CheckboxInput
      checked={highlightChecked}
      id="toggleHighlightOption"
      onChange={(e) => {
        setHighlightChecked(e.target.checked);
      }}
      label="Highlight substructure in search results."
    />
  ) : null;

  const similarityDropdownOptions = SIMILARITY_SCORE_CUTOFFS.map((cutoff) => (
    <Option
      dataTestAttrs={{component: 'ChemSearchModalOptions', element: 'option', key: `${cutoff}`}}
      value={cutoff}
      onSelect={setMinSimilarity}
      key={cutoff}
    >
      {getSimilarityOptionLabel(cutoff)}
    </Option>
  ));

  const similaritySearchOptions = (
    <FormGroup>
      <FieldLabel label="Similarity score" />
      <SelectDropdown
        alwaysDropDown={true}
        className={css(styles.similaritySearchOptions)}
        dataTestAttrs={{component: 'ChemSearchModalOptions', element: 'similarityScoreDropdown'}}
        displayName={getSimilarityOptionLabel(minSimilarity)}
        menu={
          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.SMALL} value={minSimilarity}>
            {similarityDropdownOptions}
          </Menu>
        }
      />
      <p className={css(styles.explanationText)}>
        Similarity searches are performed using{' '}
        <a
          className={css(styles.explanationTextLink)}
          href="https://jcheminf.biomedcentral.com/articles/10.1186/s13321-015-0069-3"
          target="_blank"
          rel="noopener noreferrer"
        >
          Tanimoto similarity
        </a>
        .
      </p>
    </FormGroup>
  );

  const isSubstructureSearch = chemSearchOperator === Operator.SUBSTRUCTURE;

  return isSubstructureSearch ? highlightOption : similaritySearchOptions;
}

const styles = StyleSheet.create({
  explanationText: {
    paddingTop: 12,
    color: GRAY_DARKER,
  },
  explanationTextLink: {
    color: GRAY_DARKER,
    textDecorationLine: 'underline',
  },
  similaritySearchOptions: {
    width: '50%',
  },
});
